import { DateTime, Duration } from "luxon";

import { createLogger } from "@/logger";

const log = createLogger("telemetry");

export type IdleTransition = "idle-enter" | "idle-exit" | null;

/** The window events that count as the user doing something. */
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "wheel"] as const;

const DEFAULT_IDLE_THRESHOLD = Duration.fromObject({ seconds: 30 });

export class InactivityMonitorService {
  // Epoch milliseconds rather than a DateTime, since every mouse move and scroll writes it.
  private lastActivityAt = Date.now();
  private isListening = false;
  private wasIdle = false;
  private readonly idleThresholdMs: number;

  private readonly onActivity = () => {
    this.lastActivityAt = Date.now();
  };

  /**
   * @param idleThresholdMs - How long without input before {@link isIdle} reads true.
   */
  public constructor(idleThresholdMs = DEFAULT_IDLE_THRESHOLD.toMillis()) {
    this.idleThresholdMs = idleThresholdMs;
  }

  public start(): void {
    if (this.isListening) return;
    this.isListening = true;
    log.info(`[${this.constructor.name}] Listening for activity events`);

    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, this.onActivity, { passive: true });
    }
  }

  /** Removes the listeners {@link start} added; the idle clock stops advancing on input until started again. */
  public stop(): void {
    if (!this.isListening) return;
    this.isListening = false;

    for (const event of ACTIVITY_EVENTS) {
      window.removeEventListener(event, this.onActivity);
    }
  }

  public get inactivityDurationMs(): number {
    return DateTime.now().diff(DateTime.fromMillis(this.lastActivityAt)).toMillis();
  }

  public get isIdle(): boolean {
    return this.inactivityDurationMs >= this.idleThresholdMs;
  }

  public consumeIdleTransition(): IdleTransition {
    const currentlyIdle = this.isIdle;
    const transition: IdleTransition =
      currentlyIdle && !this.wasIdle ? "idle-enter" : !currentlyIdle && this.wasIdle ? "idle-exit" : null;
    this.wasIdle = currentlyIdle;
    return transition;
  }
}
